import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTheme, ThemeColors } from '../../contexts/ThemeContext';
import { DetailView } from '../DetailView';
import type { DetailViewLayout } from '../DetailView';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { ConfirmationDialog } from '../ui';
import { useNotification } from '../../contexts/NotificationContext';
import { useCustomFieldValues } from '../../modules/custom-fields/useData';
import { useUserSettings } from '../../modules/settings/useData';
import { getEntityTypeFromRoute, getEntityTypeFromTitle } from '../../utils/entityTypeMapper';
import { useAuth } from '../../contexts/AuthContext';
import { useModuleTranslation } from '../../i18n/useModuleTranslation';
import { resolveCommonAction } from '../../i18n/labels';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const msg = (error as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (msg) return msg;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

interface GenericDetailPageProps<T> {
  // Data hook that returns { data/client, isLoading, mutate }
  useData: (id: number) => {
    data?: T | undefined;
    client?: T | undefined;
    isLoading: boolean;
    mutate: () => void | any;
  };
  // Module configuration
  config: {
    module: {
      i18nKey?: string;
      permissionModuleKey?: string;
      route: {
        list: string;
        new: string;
        detail: (id: number) => string;
        edit: (id: number) => string;
      };
    };
    detail: {
      subtitle: string;
      layout?: DetailViewLayout;
      notFoundTitle: string;
      notFoundMessage: string;
      notFoundBackText: string;
      defaultTitle: string;
      getTitle: (item: T) => string;
      /** When false, the Edit button is hidden on the detail view (e.g. read-only modules like activity logs). */
      canEdit?: boolean;
      /** Optional actions in the header next to Edit (e.g. Share on email accounts). */
      getExtraHeaderActions?: (item: T, mutate?: () => void) => React.ReactNode;
    };
    utils: {
      formatDate: (dateString?: string) => string;
    };
    getDetailSections: (
      item: T,
      formatDate: (dateString?: string) => string,
      formatCurrency?: (amount?: number | null) => string,
      mutate?: () => void,
      colors?: ThemeColors
    ) => Array<{
      title: string;
      fields?: Array<{
        label: string;
        value: any;
        condition?: boolean;
      }>;
      content?: React.ReactNode;
      condition?: boolean;
    }>;
    getStatusBadges?: (item: T, colors?: ThemeColors) => React.ReactNode;
    delete?: {
      title: string;
      message: string;
      confirmText: string;
      cancelText: string;
      errorMessage: string;
      handler: (id: number) => Promise<void>;
    };
  };
}

function GenericDetailPage<T extends { [key: string]: any }>({
  useData,
  config,
}: GenericDetailPageProps<T>) {
  const { t } = useTranslation('common');
  const i18nKey = config.module.i18nKey;
  const { mt } = useModuleTranslation(i18nKey);
  const { colors } = useTheme();
  const { userSettings } = useUserSettings();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const dataHookResult = useData(id ? parseInt(id) : 0);
  const data = (dataHookResult.data || dataHookResult.client) as T | undefined;
  const isLoading = dataHookResult.isLoading;

  const permissionModuleKey = config.module.permissionModuleKey;
  const canEdit =
    config.detail.canEdit !== false && (!permissionModuleKey || can(permissionModuleKey, 'edit'));
  const canDelete =
    Boolean(config.delete) && (!permissionModuleKey || can(permissionModuleKey, 'delete'));

  // Get the ID from the data using common ID field names (used for edit link and entity id).
  // Prefer numeric 'id' first so entities with both id and clientId (e.g. Offer) use their primary key.
  const getItemId = (item: T): number => {
    if ('id' in item && typeof (item as any).id === 'number') return (item as any).id;
    if ('todoId' in item) return (item as any).todoId;
    if ('taskId' in item) return (item as any).taskId;
    if ('leadId' in item) return (item as any).leadId;
    if ('expenseId' in item) return (item as any).expenseId;
    if ('clientAppId' in item) return (item as any).clientAppId;
    if ('ticketId' in item) return (item as any).ticketId;
    if ('paymentId' in item && typeof (item as any).paymentId === 'number') return (item as any).paymentId;
    if ('invoiceId' in item && typeof (item as any).invoiceId === 'number') return (item as any).invoiceId;
    if ('clientId' in item && typeof (item as any).clientId === 'number') return (item as any).clientId;
    return id ? parseInt(id, 10) : 0;
  };

  const handleDelete = useCallback(async () => {
    if (!config.delete || !data) return;
    const itemId = getItemId(data);
    setDeleteLoading(true);
    try {
      await config.delete.handler(itemId);
      showSuccess(t('states.deletedSuccessfully', { defaultValue: 'Item deleted successfully' }));
      setDeleteOpen(false);
      navigate(config.module.route.list);
    } catch (err: unknown) {
      showError(getErrorMessage(err, config.delete.errorMessage));
    } finally {
      setDeleteLoading(false);
    }
  }, [config, data, id, navigate, showError, showSuccess, t]);

  // Get entity type and ID for custom fields
  const entityType = useMemo(() => {
    return getEntityTypeFromRoute(config.module.route.list) || 
           getEntityTypeFromTitle((config.module as any).title || '');
  }, [config]);

  const entityId = useMemo(() => {
    if (!data) return null;
    return getItemId(data);
  }, [data, id]);

  // Fetch custom field values
  const { data: customFields } = useCustomFieldValues(
    entityType || '',
    entityId || 0
  );

  const sections = useMemo(() => {
    if (!data) return [];
    const formatCurrency = (config.utils as { formatCurrency?: (amount?: number | null) => string }).formatCurrency;
    const baseSections = config.getDetailSections(
      data,
      config.utils.formatDate,
      formatCurrency,
      dataHookResult.mutate,
      colors
    );
    
    // Add custom fields section if we have custom fields
    if (entityType && entityId && customFields && customFields.length > 0) {
      const customFieldsSection = {
        title: 'Custom Fields',
        fields: customFields.map((field) => ({
          label: field.fieldLabel,
          value: field.value !== null && field.value !== undefined 
            ? String(field.value) 
            : <span className={colors.textMuted}>-</span>,
        })),
      };
      return [...baseSections, customFieldsSection];
    }
    
    return baseSections;
  }, [data, config, entityType, entityId, customFields, dataHookResult.mutate]);

  const statusBadges = useMemo(() => {
    if (!data || !config.getStatusBadges) return null;
    return config.getStatusBadges(data, colors);
  }, [data, config, colors]);

  const clientSummaryAction = useMemo(() => {
    if (!data || config.module.route.list === '/clients') return null;
    const d = data as { clientId?: number; client?: { clientId?: number } };
    const cid = typeof d.clientId === 'number' ? d.clientId : d.client?.clientId;
    if (typeof cid !== 'number') return null;
    return (
      <Link
        to={`/clients/${cid}/summary`}
        className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-amber-600 hover:text-amber-900 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/50"
        title="Organization overview"
      >
        <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span>Summary</span>
      </Link>
    );
  }, [data, config.module.route.list, colors]);

  const moduleHeaderActions = useMemo(() => {
    if (!data || !config.detail.getExtraHeaderActions) return null;
    return config.detail.getExtraHeaderActions(data, dataHookResult.mutate);
  }, [data, config.detail, dataHookResult.mutate]);

  const extraHeaderActions = useMemo(() => {
    if (!clientSummaryAction && !moduleHeaderActions) return undefined;
    return (
      <>
        {moduleHeaderActions}
        {clientSummaryAction}
      </>
    );
  }, [clientSummaryAction, moduleHeaderActions]);

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" message={t('states.loading')} />
      </div>
    );
  }

  return (
    <>
    <DetailView
      title={
        data
          ? config.detail.getTitle(data)
          : i18nKey
            ? mt('detail.defaultTitle', config.detail.defaultTitle)
            : config.detail.defaultTitle
      }
      subtitle={i18nKey ? mt('detail.subtitle', config.detail.subtitle) : config.detail.subtitle}
      backRoute={config.module.route.list}
      editRoute={
        data ? config.module.route.edit(getItemId(data)) : (id ? config.module.route.edit(parseInt(id)) : config.module.route.list)
      }
      canEdit={canEdit}
      canDelete={canDelete}
      onDelete={canDelete ? () => setDeleteOpen(true) : undefined}
      statusBadges={statusBadges}
      sections={sections}
      layout={userSettings?.detailViewLayout ?? config.detail.layout ?? 'card'}
      isLoading={isLoading && !data}
      notFoundTitle={i18nKey ? mt('detail.notFoundTitle', config.detail.notFoundTitle) : config.detail.notFoundTitle}
      notFoundMessage={i18nKey ? mt('detail.notFoundMessage', config.detail.notFoundMessage) : config.detail.notFoundMessage}
      notFoundBackText={i18nKey ? mt('detail.notFoundBackText', config.detail.notFoundBackText) : config.detail.notFoundBackText}
      extraHeaderActions={extraHeaderActions}
    />
    {config.delete ? (
      <ConfirmationDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void handleDelete()}
        title={i18nKey ? mt('delete.title', config.delete.title) : config.delete.title}
        message={i18nKey ? mt('delete.message', config.delete.message) : config.delete.message}
        confirmText={
          i18nKey
            ? resolveCommonAction(t, mt('delete.confirmText', config.delete.confirmText))
            : resolveCommonAction(t, config.delete.confirmText)
        }
        cancelText={
          i18nKey
            ? resolveCommonAction(t, mt('delete.cancelText', config.delete.cancelText))
            : resolveCommonAction(t, config.delete.cancelText)
        }
        confirmVariant="danger"
        isLoading={deleteLoading}
      />
    ) : null}
    </>
  );
}

export default GenericDetailPage;

